/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';
import { ChooseAzureSubscriptionModal } from 'src/modules/startupConfigs/portalsSignUpOrchestrator/ChooseAzureSubscriptionModal';

/**
 * Startup config for Luis portal.
 */
export const LuisStartupConfig = observer(() => {
    const { rootStore, dispatcher, uiStore } = useGlobalState();
    const migrationInfo = rootStore.userMigrationInfoStoreHouse;
    const selectedResource = rootStore.azureStore.session.csAccountStore;
    const currentSubscriptionId = rootStore.azureStore.session.subscriptionId;

    React.useEffect(() => {
        dispatcher.dispatch('getAzureSubscriptions');
        dispatcher.dispatch('getUserMigrationInfo');
    }, []);

    React.useEffect(() => {
        const allowedResource = selectedResource && AzureResourcesStrategies.luisStrategy(selectedResource);
        const isInvalidResource = !Boolean(selectedResource?.path) || !allowedResource;

        if (isInvalidResource && uiStore.isSwitchAccountModalHidden) {
            dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true });
        }
    }, [selectedResource?.path]);

    const changeModalVisibility = (isVisible: boolean) => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible });

    const handleSelectSubscription = (subscriptionId: string) =>
        dispatcher.dispatch('switchAzureSubscriptionInSession', { id: subscriptionId });

    const fetchFeaturesSettings = () =>
        dispatcher.dispatch('fetchFeaturesSettingsPerAzureCsAccount', {
            csAccountStore: rootStore.azureStore.session.csAccountStore
        });

    if (migrationInfo.tracker.isNotStarted || migrationInfo.tracker.isRunning || migrationInfo.items.length > 0) {
        return null;
    }

    return currentSubscriptionId ? (
        <AzureResourcesManager
            serviceTarget="LUIS.Authoring"
            onSwitchResourceComplete={fetchFeaturesSettings}
            onChangeSelectResourceVisible={changeModalVisibility}
            isSelectResourceVisible={!uiStore.isSwitchAccountModalHidden}
            strategies={{ resource: AzureResourcesStrategies.luisStrategy }}
        />
    ) : (
        <ChooseAzureSubscriptionModal
            onSubmit={handleSelectSubscription}
            hidden={uiStore.isSwitchAccountModalHidden}
            onDismiss={() => changeModalVisibility(false)}
            csAccountFilterPredicate={AzureResourcesStrategies.luisStrategy}
        />
    );
});
